import { Box } from '@mui/material';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeamMembersHash, selectAuthToken } from 'selectors';
import { bulkActivateAccounts } from 'thunk';
import * as ProfileServices from 'ProfileModule/services';
import ApiClientManager from 'core/apiClient';
import { useNotification } from 'hooks/useNotification';

export interface UseBulkAccountsActivationModalOptions {
  /** Tenant name for API context - if not provided, falls back to global tenant */
  tenant?: string;
  /** Realm ID for API context - if not provided, falls back to global realm */
  realmId?: string;
}

export const useBulkAccountsActivationModal = (options?: UseBulkAccountsActivationModalOptions) => {
  const { openModal } = useDynamicModal();
  const dispatch = useDispatch();
  const membersHash = useSelector(selectTeamMembersHash);
  const token = useSelector(selectAuthToken);
  const { tenant, realmId } = options || {};
  const { showSuccess, showError } = useNotification();

  const openBulkAccountsActivationModal = useCallback(
    (args: { accountIds: number[] }) => {
      const onConfirm = async () => {
        try {
          // Use explicit tenant/realm context if provided
          if (tenant && realmId && token) {
            const headers = ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId });
            await ProfileServices.bulkActivateAccounts(
              { account_ids: args.accountIds },
              { headers },
              token,
            );
            showSuccess('Accounts activated successfully');
          } else {
            // Fall back to global context via thunk
            // TODO: Fix with Redux Toolkit configureStore migration
            dispatch(bulkActivateAccounts({ account_ids: args.accountIds }) as any);
          }
        } catch (e: any) {
          const apiMessage = e?.data?.message || e?.response?.data?.message || e?.message;
          const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '. Please try again.';
          showError(`Failed to activate accounts${apiMessageSuffix}`);
        }
      };

      openModal({
        modal: ({ toggle }) => {
          return (
            <SimpleModal
              open
              onClose={toggle}
              onConfirm={onConfirm}
              closeOnConfirm
              headerText="Bulk Accounts Activation Modal"
              Body={
                <Box>
                  <Box>These following accounts will be activated:</Box>
                  <Box component={'ul'}>
                    {args.accountIds.map((id) => (
                      <li key={id}>
                        {membersHash[id]?.name} - {id}
                      </li>
                    ))}
                  </Box>
                </Box>
              }
            />
          );
        },
      });
    },
    [dispatch, membersHash, openModal, tenant, realmId, token, showSuccess, showError],
  );
  return {
    openBulkAccountsActivationModal,
  };
};
